import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import PlotlyPlot from 'react-plotly.js'
import moment from 'moment-timezone'
import type { Config, Data, Layout } from 'plotly.js'

import alertsPlotBandBuilder from 'pages/Device/ThermostatRthPanel/Chart/alertsPlotBandBuilder'
import { updateLayoutOnResetZoom, updateLocalStorageAndGetAllTracesToDisplay } from 'pages/Device/ThermostatRthPanel/Chart/plotHelper'
import { parseRthData } from 'pages/Device/ThermostatRthPanel/Chart/rthDataParser'
import { setYaxisRange } from 'pages/Device/ThermostatRthPanel/Chart/chartOptions'

import legendDataParser from 'pages/Device/ThermostatRthPanel/Chart/legendDataparser'
import AlertsTooltip from 'pages/shared/AlertsTooltip'

import { config } from 'helpers/plotly/config'

import { NoData, screenSize } from 'styles/shared'
import { Alert, DeviceConnectionState } from 'types/Alert'

type PlotlyPoint = {
  bbox: { x0: number }
  x?: string | number | Date
}

type PlotlyMouseEvent = {
  points: PlotlyPoint[]
  event: {
    layerX: number
    preventDefault: () => void
  }
}

type PlotLayoutAxis = {
  fixedrange?: boolean
  range?: unknown[] | string
}

type PlotLayout = {
  xaxis: PlotLayoutAxis
  yaxis5: PlotLayoutAxis
  yaxis6: PlotLayoutAxis
  [key: string]: unknown
}

type ParsedRthData = {
  traces: Data[] | null
  zoneTraces: Data[] | null
  stageTraces: Data[] | null
  layout: PlotLayout | null
  alerts: Alert[] | null
  deviceConnectionStateAlerts?: DeviceConnectionState[] | null
}

type SelectedLegends = {
  alerts?: unknown
}

type RthPlotProps = {
  chartDivId: string
  deviceId: string
  startTime: string
  endTime: string
  timeZone: string
  isZoomed: boolean
  plotlyRevision: number
  freezeMouseHover?: boolean
  onHoverCallback: (cursorAt: number, layerX: number) => void
  onUnhoverCallback: (event: unknown) => void
  onRelayoutCallback: (event: unknown) => void
  onClickCallback?: (cursorAt: number | null, x0: number) => void
  onDoubleClickCallback?: () => void
  onLegendDataCallback?: (legendData: unknown, visibleAlerts: Alert[], deviceConnectionStateAlerts: DeviceConnectionState[] | null) => void
  variant?: 'normal' | 'fullscreen'
  updateGraphElementCallback?: (element: HTMLElement | undefined) => void
  selectedLegends?: SelectedLegends | null
  rthData?: unknown
  isHehm?: boolean
}

const RthPlot = ({
  deviceId,
  chartDivId,
  startTime,
  endTime,
  timeZone,
  rthData = null,
  plotlyRevision,
  isZoomed,
  freezeMouseHover = false,
  onHoverCallback,
  onUnhoverCallback,
  onRelayoutCallback,
  onLegendDataCallback = undefined,
  onClickCallback = undefined,
  onDoubleClickCallback = undefined,
  updateGraphElementCallback = undefined,
  variant = 'normal',
  selectedLegends = null,
  isHehm = false
}: RthPlotProps) => {
  const { traces, zoneTraces, stageTraces, layout, alerts, deviceConnectionStateAlerts } = useMemo(() => {
    return parseRthData(rthData, timeZone, startTime, endTime, chartDivId.replace(/-/g, ''), isHehm) as ParsedRthData
  }, [startTime, endTime, timeZone, rthData, isHehm]) // eslint-disable-line react-hooks/exhaustive-deps

  const graphRef = useRef<{ _fullLayout?: unknown } | null>(null)

  const allTraces = useMemo(() => {
    const data = updateLocalStorageAndGetAllTracesToDisplay(deviceId, selectedLegends, traces, zoneTraces, stageTraces) as Data[]

    return data
  }, [deviceId, selectedLegends, stageTraces, traces, zoneTraces])

  const updatedLayout = useMemo(() => {
    if (!layout) { return layout }

    const updatedLayout = updateLayoutOnResetZoom(isZoomed, graphRef.current?._fullLayout, layout) as PlotLayout // resets the zoom
    setYaxisRange(allTraces, updatedLayout)
    updatedLayout.xaxis.fixedrange = freezeMouseHover
    updatedLayout.yaxis5.fixedrange = freezeMouseHover
    updatedLayout.yaxis6.fixedrange = freezeMouseHover

    return updatedLayout
  }, [layout, isZoomed, freezeMouseHover, plotlyRevision, selectedLegends, allTraces]) // eslint-disable-line react-hooks/exhaustive-deps

  const xaxisRange = JSON.stringify(updatedLayout?.xaxis?.range || '')

  const updateAlerts = useCallback((selectedLegendAlerts: unknown) => {
    alertsPlotBandBuilder.addOrUpdateAlerts('rth-alerts-group', updatedLayout, chartDivId, alerts, selectedLegendAlerts, deviceConnectionStateAlerts)
  }, [xaxisRange, chartDivId, alerts]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (graphRef.current != null) {
      updateAlerts(selectedLegends?.alerts)
    }
  }, [plotlyRevision, selectedLegends?.alerts, updateAlerts, isZoomed])

  useEffect(() => {
    if (onLegendDataCallback && updatedLayout) {
      onLegendDataCallback(legendDataParser.parseLegendData(allTraces, rthData), legendDataParser.alertsWithinVisibleRange(updatedLayout, alerts), deviceConnectionStateAlerts ?? null)
    }
  }, [allTraces, updatedLayout, rthData, alerts, onLegendDataCallback]) // eslint-disable-line react-hooks/exhaustive-deps

  const closestPointToCursor = (e: PlotlyMouseEvent): PlotlyPoint | null => {
    if (e.points.length === 0) {
      return null
    }

    return e.points.reduce((prev, curr) => Math.abs(curr.bbox.x0 - e.event.layerX) < Math.abs(prev.bbox.x0 - e.event.layerX) ? curr : prev)
  }

  const timeAtCursor = (e: PlotlyMouseEvent): number | null => {
    e.event.preventDefault()
    const closestPoint = closestPointToCursor(e)
    if (e.points.length > 0 && closestPoint?.x) {
      return moment.tz(closestPoint.x, timeZone).unix() * 1000
    }

    return null
  }

  const onHover = (e: PlotlyMouseEvent) => {
    const cursorAt = timeAtCursor(e)
    if (cursorAt) {
      onHoverCallback(cursorAt, e.event.layerX)
    }
  }

  const onClick = (e: PlotlyMouseEvent) => {
    if (onClickCallback) {
      const closestPoint = closestPointToCursor(e)
      if (closestPoint) {
        onClickCallback(timeAtCursor(e), closestPoint.bbox.x0)
      }
    }
  }

  const onChartLoad = (element: HTMLElement | undefined) => {
    graphRef.current = element as { _fullLayout?: unknown }
    if (updateGraphElementCallback) {
      updateGraphElementCallback(element)
    }

    updateAlerts(selectedLegends?.alerts)
  }

  if (!updatedLayout || !rthData) {
    return (
      <S.NoData>
        <h1>No Data</h1>
        <h2>There is no data for this day.</h2>
      </S.NoData>
    )
  } else {
    return (
      <div data-testid={chartDivId}>
        <S.Plot
          divId={chartDivId}
          variant={variant}
          data={allTraces}
          revision={plotlyRevision}
          layout={updatedLayout as Partial<Layout>}
          onHover={(e) => onHover(e as unknown as PlotlyMouseEvent)}
          onUnhover={onUnhoverCallback}
          onClick={(e) => onClick(e as unknown as PlotlyMouseEvent)}
          onDoubleClick={onDoubleClickCallback as (() => void) | undefined}
          config={config as Partial<Config>}
          onRelayout={onRelayoutCallback}
          onInitialized={(_, element) => onChartLoad(element as HTMLElement)}
          useResizeHandler
        />

        <AlertsTooltip alerts={alerts || []} deviceConnectionState={deviceConnectionStateAlerts} />
      </div>
    )
  }
}

export default RthPlot

const S = {
  NoData: styled(NoData)`
    min-height: 500px;

    @media ${screenSize.small} {
      min-height: 0;
      padding: 3.3em;
    }
  `,
  Plot: styled(PlotlyPlot) < { variant: 'normal' | 'fullscreen' } >`
    width: 100%;
    height: ${({ variant }) => variant === 'normal' ? '400px' : '750px'};
  `
}
